import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, FormInstance, message } from 'antd';
import dayjs from 'dayjs';
import useListPacksQuery from 'apollo/resolvers/packs/list';
import useListEntranceTicketTypesQuery from 'apollo/resolvers/entranceTicketTypes/list';
import useListParkAvailabilityQuery from 'apollo/resolvers/parkAvailability/list';
import useListActivitiesQuery from 'apollo/resolvers/activities/list';
import useGetPromotionCodeQuery from 'apollo/resolvers/promotionCodes/get';
import useGetGiftCardQuery from 'apollo/resolvers/giftCards/get';
import { hideNavBar } from 'apollo/cache';
import useUpsertMercadoPagoTransactionMutation from 'apollo/resolvers/mercadopagoTransaction/upsert';
import purchaseDataToPurchaseInput from 'apollo/mapper/puchaseDataToPurchaseInput';
import redirectToMercadoPago from 'utils/mercadopago/redirectToMercadoPago';
import captureEvent, { captureClientSidePageview } from 'utils/post-hog';
import * as events from 'utils/post-hog/events';
import {
  Activity,
  BillingDocumentType,
  BillingSystemInputArgs,
  CustomerType,
  GiftCardWithRemainingAmount,
  PromotionCode,
  TicketFeature,
  UpsertClientArgs,
} from 'apollo/types';
import { priceWithDiscount, grillTotalPrice } from 'utils/helpers/discountValue';
import { ACTIVITY_TYPE_IDS } from 'utils/constants/activityTypes';
import {
  getPurchase,
  setPurchase,
  startPurchaseOnDate,
  usePurchase,
} from 'storage/purchaseStore';
import { ActivityPurchase, PackOrEntrancePurchase, PurchaseData } from 'storage/types';
import getTicketDisplayName from 'utils/helpers/getTicketDisplayName';
import getIncludedFeatures from 'utils/helpers/getIncludedFeatures';
import getVisitDateParts from 'utils/dates/getVisitDateParts';
import formatHourRange, { formatHour } from 'utils/formats/formatHourRange';
import placeholderImage from 'assets/images/placeholder-image.png';
import mercadoPagoLogo from 'assets/images/payment_methods/mercado_pago_logo.png';

export interface TicketSummaryItem {
  id: string;
  name: string;
  adultTicketCount: number;
  otherTicketCount: number;
  ticketCount: number;
  price: number;
}

export interface ContactFormValues {
  billingDocumentType?: BillingDocumentType;
  customerType?: CustomerType;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  confirmEmail?: string;
  documentType?: boolean;
  documentNumber?: string;
  customerBusinessActivity?: string;
  customerCityId?: number;
  customerCommuneId?: number;
  customerAddress?: string;
  acceptParkPolitics?: boolean;
  acceptValuablesPolicy?: boolean;
}

export interface ConfirmationContact {
  document: string;
  fullName: string;
  email: string;
  phone: string;
}

export interface PurchaseConfirmationData {
  image: string;
  ticketName: string;
  features: TicketFeature[];
  ticketCounts: string[];
  date: string;
  parkHours: string;
  aperture: string;
  aviaryHours: string;
  contact: ConfirmationContact;
}

export interface DiscountCardState {
  promotionCodeLabel: string;
  hasGiftCard: boolean;
  giftCardBalance: number;
  isComplete: boolean;
  error: string;
  loading: boolean;
  onApply: (code: string, password: string) => Promise<void>;
  onRemovePromotionCode: () => void;
  onRemoveGiftCard: () => void;
}

export interface GrillOption {
  id: string;
  name: string;
  disabled: boolean;
}

export interface CanopyServiceState {
  price: number;
  count: number;
  max: number;
  isApplied: boolean;
  unavailableReason?: 'disabled' | 'soldOut';
  loading: boolean;
  onAdd: () => void;
  onChangeCount: (count: number) => void;
}

export interface AdditionalServicesState {
  grills: GrillOption[];
  price: number;
  selectedId?: string;
  isApplied: boolean;
  unavailableReason?: 'disabled' | 'reserved';
  loading: boolean;
  onSelect: (id: string) => void;
  onToggle: () => void;
  canopy: CanopyServiceState;
}

export interface PaymentMethod {
  id: string;
  logo: string;
}

export interface TicketSummaryViewModel {
  items: TicketSummaryItem[];
  grill: ActivityPurchase | null;
  canopy: ActivityPurchase | null;
  subTotal: number;
  discountAmount: number;
  giftCardAmount: number;
  total: number;
  discountCard: DiscountCardState;
  additionalServices: AdditionalServicesState;
  paymentMethods: PaymentMethod[];
  selectedPaymentMethodId: string;
  onSelectPaymentMethod: (id: string) => void;
  canPay: boolean;
  loading: boolean;
  visitDate: string;
  showConfirmation: boolean;
  confirmation: PurchaseConfirmationData;
  form: FormInstance;
  onValuesChange: (values: ContactFormValues) => void;
  onIncompleteSubmit: () => void;
  onFinish: (values: ContactFormValues) => void;
  onPay: () => void;
  goBack: () => void;
}

const PAYMENT_METHODS: PaymentMethod[] = [{ id: 'mercadopago', logo: mercadoPagoLogo }];

export const CONTACT_FORM_INITIAL_VALUES: ContactFormValues = {
  billingDocumentType: BillingDocumentType.Voucher,
  customerType: CustomerType.Person,
  documentType: false,
};

const AVIARY_NAME = 'aviario';

const toSummaryItem = (item: PackOrEntrancePurchase): TicketSummaryItem => ({
  id: item.id,
  name: item.name,
  adultTicketCount: item.adultTicketCount,
  otherTicketCount: item.otherTicketCount,
  ticketCount: item.adultTicketCount + item.otherTicketCount,
  price:
    item.adultValue * item.adultTicketCount + item.otherValue * item.otherTicketCount,
});

const dayKey = (isoDate?: string): string => (isoDate ? isoDate.split('T')[0] : '');

const isFilled = (value?: string): boolean => Boolean(value && value.trim());

const isSelected = (value?: number): boolean => Boolean(value);

const isBookable = (activity: Activity): boolean =>
  Boolean(activity.activityTimes[0]?.remaining);

const totalWithPromotionCode = (
  purchase: PurchaseData | null,
  promotionCode: PromotionCode | null
): number => {
  if (!purchase) return 0;
  if (!promotionCode) return purchase.totalPrice;
  return (
    priceWithDiscount(purchase.entrances, promotionCode.promotionCodeEntrances) +
    priceWithDiscount(purchase.packs, [], promotionCode.promotionCodePacks) +
    grillTotalPrice(purchase.grill, promotionCode.promotionCodeActivities) +
    grillTotalPrice(purchase.canopy || null, promotionCode.promotionCodeActivities)
  );
};

const isContactComplete = (values: ContactFormValues): boolean => {
  const isInvoice = values.billingDocumentType === BillingDocumentType.Invoice;
  const isCompany = isInvoice && values.customerType === CustomerType.Company;

  const baseComplete =
    Boolean(values.billingDocumentType) &&
    isFilled(values.firstName) &&
    (isCompany || isFilled(values.lastName)) &&
    isFilled(values.phone) &&
    isFilled(values.email) &&
    values.email?.trim() === values.confirmEmail?.trim() &&
    isFilled(values.documentNumber) &&
    Boolean(values.acceptParkPolitics) &&
    Boolean(values.acceptValuablesPolicy);

  if (!isInvoice) return baseComplete;

  return (
    baseComplete &&
    Boolean(values.customerType) &&
    isFilled(values.customerBusinessActivity) &&
    isSelected(values.customerCityId) &&
    isSelected(values.customerCommuneId) &&
    isFilled(values.customerAddress)
  );
};

const useTicketSummaryViewModel = (): TicketSummaryViewModel => {
  const history = useHistory();
  const purchase = usePurchase();
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();

  const [values, setValues] = useState<ContactFormValues>(CONTACT_FORM_INITIAL_VALUES);
  const [hasRevealed, setHasRevealed] = useState(false);
  const [promotionCode, setPromotionCode] = useState<PromotionCode | null>(null);
  const [promotionCodeLabel, setPromotionCodeLabel] = useState('');
  const [giftCard, setGiftCard] = useState<GiftCardWithRemainingAmount | null>(null);
  const [discountError, setDiscountError] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);
  const [selectedGrillId, setSelectedGrillId] = useState<string>();
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(
    PAYMENT_METHODS[0].id
  );

  const [getPromotionCode] = useGetPromotionCodeQuery();
  const [getGiftCard] = useGetGiftCardQuery();
  const [createPurchase, { loading: paying }] = useUpsertMercadoPagoTransactionMutation();

  const packItem = purchase?.packs[0];
  const entranceItem = purchase?.entrances[0];
  const item = packItem || entranceItem;

  const { data: packsData, loading: loadingPacks } = useListPacksQuery();
  const { data: entrancesData, loading: loadingEntrances } =
    useListEntranceTicketTypesQuery();
  const { data: parkData, loading: loadingPark } = useListParkAvailabilityQuery();
  const { data: grillsData, loading: loadingGrills } = useListActivitiesQuery(
    purchase?.date || '',
    ACTIVITY_TYPE_IDS.grill,
    !purchase?.date
  );
  const { data: canopyData, loading: loadingCanopy } = useListActivitiesQuery(
    purchase?.date || '',
    ACTIVITY_TYPE_IDS.canopy,
    !purchase?.date
  );

  const aviaryActivity = item?.activities.find((activity) =>
    activity.name.toLowerCase().includes(AVIARY_NAME)
  );

  const staticPack = packsData?.listPacks.find((pack) => pack.id === item?.id);
  const staticEntrance = entrancesData?.listEntranceTicketTypes.find(
    (entrance) => entrance?.id === item?.id
  );
  const images = staticPack?.packImages || staticEntrance?.entranceImages || [];

  const parkSeason = parkData?.listParkAvailability.find(
    (season) =>
      dayKey(season.startDate) <= dayKey(purchase?.date) &&
      dayKey(purchase?.date) <= dayKey(season.endDate)
  );

  const items = [...(purchase?.packs || []), ...(purchase?.entrances || [])].map(
    toSummaryItem
  );
  const subTotal = purchase?.totalPrice || 0;

  const discountedTotal = totalWithPromotionCode(purchase, promotionCode);

  const discountAmount = subTotal - discountedTotal;
  const giftCardAmount = giftCard
    ? Math.min(giftCard.remainingAmount, discountedTotal)
    : 0;
  const total = discountedTotal - giftCardAmount;

  const applyPromotionCode = async (code: string) => {
    const response = await getPromotionCode({ variables: { code } });
    const found = response.data?.getPromotionCode;
    if (!found) {
      setDiscountError(t('pages.ticketSummary.discount.invalidCode'));
      return;
    }
    const visitDay = new Date(purchase?.date || '').getDay();
    const isWeekend = visitDay === 0 || visitDay === 6;
    if (!found.forWeekends && isWeekend) {
      setDiscountError(t('pages.ticketSummary.discount.notForWeekends'));
      return;
    }
    if (totalWithPromotionCode(purchase, found) >= subTotal) {
      setDiscountError(t('pages.ticketSummary.discount.notApplicable'));
      return;
    }
    setPromotionCode(found);
    setPromotionCodeLabel(code);
    const current = getPurchase();
    if (current) setPurchase({ ...current, code });
  };

  const applyGiftCard = async (code: string, password: string) => {
    const response = await getGiftCard({ variables: { code, password } });
    const found = response.data?.getGiftCard;
    if (!found) {
      setDiscountError(t('pages.ticketSummary.discount.invalidGiftCard'));
      return;
    }
    setGiftCard(found);
    const current = getPurchase();
    if (current) {
      setPurchase({
        ...current,
        giftCard: { giftCardCode: code, giftCardPassword: password },
      });
    }
  };

  const onApplyDiscount = async (code: string, password: string) => {
    setDiscountError('');
    setDiscountLoading(true);
    try {
      if (password) {
        await applyGiftCard(code, password);
      } else {
        await applyPromotionCode(code);
      }
    } finally {
      setDiscountLoading(false);
    }
  };

  const onRemovePromotionCode = () => {
    setPromotionCode(null);
    setPromotionCodeLabel('');
    setDiscountError('');
    const current = getPurchase();
    if (current) setPurchase({ ...current, code: '' });
  };

  const onRemoveGiftCard = () => {
    setGiftCard(null);
    setDiscountError('');
    const current = getPurchase();
    if (current) setPurchase({ ...current, giftCard: undefined });
  };

  const sellableGrills = (grillsData?.listActivities || []).filter(
    (activity) => activity.status
  );
  const grillOptions: GrillOption[] = sellableGrills.map((activity) => ({
    id: activity.id,
    name: activity.name,
    disabled: !isBookable(activity),
  }));

  const unavailableReason = ((): AdditionalServicesState['unavailableReason'] => {
    if (loadingGrills) return undefined;
    if (!sellableGrills.length) return 'disabled';
    if (grillOptions.every((option) => option.disabled)) return 'reserved';
    return undefined;
  })();

  const applyGrill = (id: string) => {
    const activity = sellableGrills.find((grill) => grill.id === id);
    const current = getPurchase();
    if (!activity || !current) return;
    if (!current.grill) {
      captureEvent(events.grillAdded, {
        flow: events.flows.purchase,
        grill_name: activity.name,
      });
    }
    setPurchase({
      ...current,
      grill: {
        id: activity.id,
        ticketCount: 1,
        activityTimeId: activity.activityTimes[0]?.id || '',
        name: activity.name,
        value: activity.value,
      },
    });
  };

  const onSelectGrill = (id: string) => {
    setSelectedGrillId(id);
    if (purchase?.grill) applyGrill(id);
  };

  const onToggleGrill = () => {
    if (purchase?.grill) {
      const current = getPurchase();
      if (current) setPurchase({ ...current, grill: null });
      setSelectedGrillId(undefined);
      captureEvent(events.grillRemoved, { flow: events.flows.purchase });
      return;
    }
    if (selectedGrillId) applyGrill(selectedGrillId);
  };

  const canopyActivity = (canopyData?.listActivities || []).find(
    (activity) => activity.status
  );
  const canopyRemaining = canopyActivity?.activityTimes[0]?.remaining || 0;
  const visitorCount = [
    ...(purchase?.packs || []),
    ...(purchase?.entrances || []),
  ].reduce((count, entry) => count + entry.adultTicketCount + entry.otherTicketCount, 0);
  const maxCanopy = Math.min(visitorCount, canopyRemaining);

  const canopyUnavailableReason = ((): CanopyServiceState['unavailableReason'] => {
    if (loadingCanopy) return undefined;
    if (!canopyActivity) return 'disabled';
    if (!canopyRemaining) return 'soldOut';
    return undefined;
  })();

  const setCanopyCount = (count: number) => {
    const current = getPurchase();
    if (!canopyActivity || !current) return;
    const ticketCount = Math.min(Math.max(count, 0), maxCanopy);
    const previousCount = current.canopy?.ticketCount || 0;
    if (ticketCount !== previousCount) {
      captureEvent(
        ticketCount > previousCount ? events.canopyAdded : events.canopyRemoved,
        {
          flow: events.flows.purchase,
          count: ticketCount,
          reason: 'user',
        }
      );
    }
    setPurchase({
      ...current,
      canopy: ticketCount
        ? {
            id: canopyActivity.id,
            ticketCount,
            activityTimeId: canopyActivity.activityTimes[0]?.id || '',
            name: canopyActivity.name,
            value: canopyActivity.value,
          }
        : null,
    });
  };

  const paymentEventProperties = () => {
    const current = getPurchase();
    return {
      flow: events.flows.purchase,
      total,
      canopy_count: current?.canopy?.ticketCount || 0,
      has_grill: Boolean(current?.grill),
      has_promo_code: Boolean(promotionCode),
      has_gift_card: Boolean(giftCard),
    };
  };

  const pay = async (formValues: ContactFormValues) => {
    const current = getPurchase();
    if (!current) return;
    captureEvent(events.paymentStarted, paymentEventProperties());

    const isCompany =
      formValues.billingDocumentType === BillingDocumentType.Invoice &&
      formValues.customerType === CustomerType.Company;

    const clientArguments: UpsertClientArgs = {
      firstName: formValues.firstName || '',
      lastName: isCompany ? undefined : formValues.lastName,
      phone: formValues.phone || '',
      email: (formValues.email || '').trim(),
      documentNumber: formValues.documentNumber || '',
      documentIsPassport: Boolean(formValues.documentType),
    };

    const billingSystemArguments: BillingSystemInputArgs = {
      billingDocumentType: formValues.billingDocumentType as BillingDocumentType,
      customerType: formValues.customerType,
      customerBusinessActivity: formValues.customerBusinessActivity,
      customerAddress: formValues.customerAddress,
      customerCityId: formValues.customerCityId,
      customerCommuneId: formValues.customerCommuneId,
    };

    const purchaseArguments = purchaseDataToPurchaseInput(
      current,
      current.date,
      selectedPaymentMethodId,
      total
    );

    const response = await createPurchase({
      variables: { clientArguments, purchaseArguments, billingSystemArguments },
    });
    const result = response.data?.createPurchase;
    if (!result) return;

    startPurchaseOnDate(current.date);
    if (result.token) redirectToMercadoPago(result.url);
    else history.push(result.url);
  };

  const visitDate = purchase?.date
    ? t('pages.ticketSummary.visitDate', getVisitDateParts(purchase.date, i18n.language))
    : '';

  const adultCount = item?.adultTicketCount || 0;
  const otherCount = item?.otherTicketCount || 0;

  const ticketCounts: string[] = [];
  if (adultCount) {
    ticketCounts.push(
      adultCount === 1
        ? t('pages.ticketSummary.confirmation.adultSingular', { count: adultCount })
        : t('pages.ticketSummary.confirmation.adultPlural', { count: adultCount })
    );
  }
  if (otherCount) {
    ticketCounts.push(
      otherCount === 1
        ? t('pages.ticketSummary.confirmation.otherSingular', { count: otherCount })
        : t('pages.ticketSummary.confirmation.otherPlural', { count: otherCount })
    );
  }

  const fullName = [values.firstName, values.lastName]
    .filter((part) => isFilled(part))
    .join(' ');

  const confirmation: PurchaseConfirmationData = {
    image: images[0]?.image || placeholderImage,
    ticketName: getTicketDisplayName(item?.name),
    features: getIncludedFeatures(staticPack?.features || staticEntrance?.features),
    ticketCounts,
    date: purchase?.date ? dayjs(dayKey(purchase.date)).format('DD - MM - YYYY') : '',
    parkHours: formatHourRange(parkSeason?.aperture, parkSeason?.closure),
    aperture: formatHour(parkSeason?.aperture),
    aviaryHours: formatHourRange(aviaryActivity?.startHour, aviaryActivity?.endHour),
    contact: {
      document: values.documentNumber || '',
      fullName,
      email: values.email || '',
      phone: values.phone || '',
    },
  };

  useEffect(() => {
    if (isContactComplete(values)) setHasRevealed(true);
  }, [values]);

  useEffect(() => {
    const current = getPurchase();
    if (loadingCanopy || !current?.canopy || current.canopy.ticketCount <= maxCanopy)
      return;
    captureEvent(events.canopyRemoved, {
      flow: events.flows.purchase,
      count: maxCanopy,
      reason: 'availability',
    });
    setPurchase({
      ...current,
      canopy: maxCanopy ? { ...current.canopy, ticketCount: maxCanopy } : null,
    });
  }, [loadingCanopy, maxCanopy]);

  useEffect(() => {
    hideNavBar(true);
    return () => hideNavBar(false);
  }, []);

  useEffect(() => {
    captureClientSidePageview();
  }, []);

  return {
    items,
    grill: purchase?.grill || null,
    canopy: purchase?.canopy || null,
    subTotal,
    discountAmount,
    giftCardAmount,
    total,
    discountCard: {
      promotionCodeLabel,
      hasGiftCard: Boolean(giftCard),
      giftCardBalance: giftCard?.remainingAmount || 0,
      isComplete: Boolean(promotionCode) && Boolean(giftCard),
      error: discountError,
      loading: discountLoading,
      onApply: onApplyDiscount,
      onRemovePromotionCode,
      onRemoveGiftCard,
    },
    additionalServices: {
      grills: grillOptions,
      price: sellableGrills[0]?.value || 0,
      selectedId: purchase?.grill?.id || selectedGrillId,
      isApplied: Boolean(purchase?.grill),
      unavailableReason,
      loading: loadingGrills,
      onSelect: onSelectGrill,
      onToggle: onToggleGrill,
      canopy: {
        price: canopyActivity?.value || 0,
        count: purchase?.canopy?.ticketCount || 0,
        max: maxCanopy,
        isApplied: Boolean(purchase?.canopy),
        unavailableReason: canopyUnavailableReason,
        loading: loadingCanopy,
        onAdd: () => setCanopyCount(maxCanopy),
        onChangeCount: setCanopyCount,
      },
    },
    paymentMethods: PAYMENT_METHODS,
    selectedPaymentMethodId,
    onSelectPaymentMethod: setSelectedPaymentMethodId,
    canPay: items.length > 0,
    loading: loadingPacks || loadingEntrances || loadingPark || paying,
    visitDate,
    showConfirmation: hasRevealed,
    confirmation,
    form,
    onValuesChange: setValues,
    onIncompleteSubmit: () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      message.error(t('pages.ticketSummary.form.incompleteWarning'));
    },
    onFinish: (formValues: ContactFormValues) => {
      pay(formValues).catch(() => undefined);
    },
    onPay: () => {
      form.submit();
      captureEvent(events.toPaymentServiceButtonClicked, paymentEventProperties());
    },
    goBack: () => history.goBack(),
  };
};

export default useTicketSummaryViewModel;
